/**
 * Gnosis Pay card-spend classifier (BRIEF §7).
 *
 * VERIFIED onchain pattern (docs/RESEARCH.md, live-checked 2026-08-10):
 * a card authorisation settles onchain within ~3 minutes as
 *
 *   relayer EOA → Spender Modifier.spend() → Spender Safe
 *     → user's Roles Modifier.execTransactionWithRole(SPENDING_ROLE)
 *     → user's Safe → token.transfer(settlementSafe, amount)
 *
 * i.e. the stable, load-bearing signal is an ERC-20 Transfer of a spend token
 * FROM the user's Safe TO the Gnosis Pay settlement Safe (0x4822521E…, the
 * sole card-spend destination in live traffic and the constant Gnosis Pay's
 * own UI classifies with). Refunds/reversals are transfers from that same
 * address back INTO the Safe. User withdrawals go elsewhere and are excluded.
 *
 * We classify a Transfer log as a card spend only when ALL of:
 *   1. token is a known Gnosis Pay spend token (GBPe/EURe/USDC.e)
 *   2. `from` is the user's Gnosis Pay Safe
 *   3. `to` is in the settlement set — OR, only if the settlement set were
 *      ever emptied (contract migration), a fallback module-execution
 *      heuristic marks it "probable" (disabled by `strict`).
 *
 * The address sets live in config.GNOSIS_PAY so a Gnosis Pay migration is a
 * config change, not a code change.
 */
import { type Hex } from "../types";
import { GNOSIS_PAY, GNOSIS_SPEND_TOKENS, type SpendToken } from "../config";

/** keccak256("Transfer(address,address,uint256)") */
export const TRANSFER_TOPIC =
  "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef" as const;

export interface TransferLogLike {
  address: Hex; // token contract
  topics: readonly Hex[];
  data: Hex;
  transactionHash: Hex;
  logIndex: number;
  blockNumber: bigint | number;
}

export interface TxContext {
  /** Outer transaction `to` (module / relayer target). */
  txTo?: Hex | null;
  /** Outer transaction sender. */
  txFrom?: Hex;
}

export type SpendClassification =
  | { kind: "card_spend"; token: SpendToken; amountUnits: bigint; confidence: "high" | "probable" }
  | { kind: "refund"; token: SpendToken; amountUnits: bigint }
  | { kind: "not_card_spend"; reason: string };

const eq = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

function topicToAddress(topic: Hex): Hex {
  return `0x${topic.slice(26)}` as Hex;
}

export function classifyTransferLog(
  log: TransferLogLike,
  safeAddress: Hex,
  ctx: TxContext = {},
  opts: { strict?: boolean } = {},
): SpendClassification {
  if (log.topics[0] !== TRANSFER_TOPIC || log.topics.length < 3) {
    return { kind: "not_card_spend", reason: "not an ERC20 Transfer" };
  }

  const token = GNOSIS_SPEND_TOKENS.find((t) => eq(t.address, log.address));
  if (!token) return { kind: "not_card_spend", reason: "not a known spend token" };

  const from = topicToAddress(log.topics[1]);
  const to = topicToAddress(log.topics[2]);
  const amountUnits = BigInt(log.data);
  if (amountUnits === 0n) return { kind: "not_card_spend", reason: "zero amount" };

  const settlements = GNOSIS_PAY.settlementAddresses as readonly Hex[];
  const isSettlement = (a: Hex) => settlements.some((s) => eq(s, a));

  // Refund: settlement infrastructure sending back INTO the Safe.
  if (eq(to, safeAddress)) {
    if (isSettlement(from)) return { kind: "refund", token, amountUnits };
    return { kind: "not_card_spend", reason: "inbound transfer (deposit)" };
  }

  if (!eq(from, safeAddress)) {
    return { kind: "not_card_spend", reason: "not from the watched Safe" };
  }

  // Outgoing from the Safe. Card spend vs user withdrawal:
  if (isSettlement(to)) {
    return { kind: "card_spend", token, amountUnits, confidence: "high" };
  }

  if (settlements.length > 0 || opts.strict) {
    // We know the settlement set (or are in strict mode) and this isn't in it
    // → user withdrawal / internal move, NOT a spend.
    return { kind: "not_card_spend", reason: "outgoing transfer to non-settlement address" };
  }

  // Settlement set unknown (research pending): fall back to the module-path
  // heuristic — spend txs are submitted by Gnosis Pay, so the outer tx is not
  // sent by the Safe owner and targets a module, not the token directly.
  const moduleExecution =
    !!ctx.txTo && !eq(ctx.txTo, log.address) && !eq(ctx.txTo, safeAddress) && !eq(ctx.txFrom, safeAddress);
  if (moduleExecution) {
    return { kind: "card_spend", token, amountUnits, confidence: "probable" };
  }
  return { kind: "not_card_spend", reason: "cannot confirm Gnosis Pay module execution" };
}
